import {
  AcceptConsentResponse,
  AcceptLoginRequest,
  AcceptLoginResponse,
  AcceptLogoutResponse,
  HydraShell,
  InitialConsentResponse,
  InitialLoginResponse,
  InitialLogoutResponse,
} from '@weave/schema-gen-ts/dist/schemas/hydra-shell/hydra_shell.pb';
import { navigateToUrl, hydraSessionStore } from '@frontend/auth';
import { getDecodedWeaveToken, AuthStorage } from '@frontend/auth-helpers';
import { authorizedFetch, baseFetch } from './fetch-wrapper';

export type HydraShellServiceState = 'login' | 'consent' | 'logout' | 'none';

class HydraShellService {
  private challenge: string | undefined;
  private cancel: string | undefined;

  // getCurrentState will attempt to identify if we are starting a new login flow, or if we are in the middle of a login flow
  // URL parameters take precedence over storage values
  public getCurrentState(): HydraShellServiceState {
    // First check URL parameters
    const urlState = this.getCurrentStateFromUrl();
    // Fallback to storage
    const storageState = this.getCurrentStateFromStorage();

    return urlState !== 'none' ? urlState : storageState;
  }

  // URL params are mutually exclusive, so we can check them in any order.
  // They take precedence over storage values because if there is a URL param,
  // it means we are at the beginning of a new flow, and any existing storage values are likely from a previous flow and should be ignored.
  private getCurrentStateFromUrl(): HydraShellServiceState {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has(AuthStorage.consent_challenge)) {
      return 'consent';
    }
    if (urlParams.has(AuthStorage.login_challenge)) {
      return 'login';
    }
    if (urlParams.has(AuthStorage.logout_challenge)) {
      return 'logout';
    }
    return 'none';
  }

  private getCurrentStateFromStorage(): HydraShellServiceState {
    if (hydraSessionStore.getItem<string>(AuthStorage.consent_challenge)) {
      return 'consent';
    }
    if (hydraSessionStore.getItem<string>(AuthStorage.login_challenge)) {
      return 'login';
    }
    if (hydraSessionStore.getItem<string>(AuthStorage.logout_challenge)) {
      return 'logout';
    }
    return 'none';
  }

  public async initializeOAuth2Flow(): Promise<InitialLoginResponse> {
    this.challenge = this.getLoginChallenge();
    const res = await HydraShell.InitialOAuth2LoginRequest(baseFetch<InitialLoginResponse>(), {
      challenge: this.challenge,
    });
    hydraSessionStore.setItem(AuthStorage.oauth_client_info, {
      clientId: res.client?.clientId,
      clientName: res.client?.clientName,
    });
    if (!!res.subject) {
      this.cancel = res.subject;
    }
    return res;
  }

  public async acceptOAuth2Flow(): Promise<AcceptLoginResponse> {
    const decodedWeaveToken = getDecodedWeaveToken();
    const challenge = this.challenge || this.getLoginChallenge();
    if (!challenge) {
      throw new Error('No challenge found');
    }
    // success logging in the token is stored in local storage
    const request: AcceptLoginRequest = {
      challenge: this.challenge || this.getLoginChallenge(),
      subject: decodedWeaveToken?.username,
      rememberFor: 4 * 60 * 60, // When the session expires, in seconds. Set this to 0 so it will never expire. Backend may or may not prevent this.
      // From the Hydra docs:
      // Sets which "level" (e.g. 2-factor authentication) of authentication the user has. The value is really arbitrary
      // and optional. In the context of OpenID Connect, a value of 0 indicates the lowest authorization level.
      // acr: '0',
      // If that variable is not set, the ACR value will be set to the default passed here ('0')
      acr: '0',
      cancel: '',
    };
    if (!!this.cancel && request.subject !== this.cancel) {
      request.cancel = this.cancel;
    }

    try {
      const res = await HydraShell.AcceptOAuth2LoginRequest(authorizedFetch<AcceptLoginResponse>(), request);
      return res;
    } catch (err: any) {
      // if error contains 'login request was cancelled' then we need to redirect to the originating app in order to get a new login challenge
      // this is because the user previously logged in is different from the current user and hydra will need a different session token
      // this type of error has special handling elsewhere
      console.error(err);
      if (err.message?.includes('login request was cancelled')) {
        throw new Error('login request was cancelled');
      }
      throw err; // rethrow the error so the caller can handle it
    } finally {
      hydraSessionStore.removeItem(AuthStorage.login_challenge);
    }
  }

  public async initializeConsentFlow(challenge?: string): Promise<InitialConsentResponse> {
    this.challenge = challenge ?? this.getConsentChallenge();
    return await HydraShell.InitialOAuth2ConsentRequest(authorizedFetch<InitialConsentResponse>(), {
      challenge: this.challenge,
    });
  }

  /**
   * Accept the consent flow by providing the requested scopes and client ID.
   * Does a top level navigation to the redirect URL upon success, so anything after calling this function may not execute.
   */
  public async acceptConsentFlow(
    scope: string[],
    clientID: string | undefined,
    challenge?: string
  ): Promise<AcceptConsentResponse> {
    this.challenge = challenge ?? this.getConsentChallenge();
    const res = await HydraShell.AcceptOAuth2ConsentRequest(authorizedFetch<AcceptConsentResponse>(), {
      challenge: this.challenge,
      scope: scope,
      clientId: clientID,
    });
    hydraSessionStore.removeItem(AuthStorage.consent_challenge);
    if (!!res.redirectTo) {
      navigateToUrl(res.redirectTo, { reason: 'acceptConsentFlow' });
    }
    return res;
  }

  public async initializeLogoutFlow(): Promise<InitialLogoutResponse> {
    return await HydraShell.InitialOAuth2LogoutRequest(baseFetch<InitialLogoutResponse>(), {
      challenge: this.getLogoutChallenge(),
    });
  }

  public async acceptLogoutFlow(): Promise<AcceptLogoutResponse> {
    //TODO: sign out of jackson?
    //https://boxyhq.com/docs/jackson/sso-flow#4-saml-slo
    try {
      const res = await HydraShell.AcceptOAuth2LogoutRequest(baseFetch<AcceptLogoutResponse>(), {
        challenge: this.getLogoutChallenge(),
      });
      return res;
    } finally {
      hydraSessionStore.clear();
    }
  }

  public getLoginChallenge(): string {
    // get challenge from query string param named 'login_challenge'
    const urlParams = new URLSearchParams(window.location.search);
    const challenge = urlParams.get(AuthStorage.login_challenge);
    if (!!challenge) {
      hydraSessionStore.setItem(AuthStorage.login_challenge, challenge);
      return challenge;
    } else {
      const challengeFromLocalStorage = hydraSessionStore.getItem<string>(AuthStorage.login_challenge);
      if (!!challengeFromLocalStorage) {
        return challengeFromLocalStorage;
      }
    }
    return '';
  }

  private getConsentChallenge(): string {
    // get challenge from query string param named 'consent_challenge'
    const urlParams = new URLSearchParams(window.location.search);
    const challenge = urlParams.get(AuthStorage.consent_challenge);
    if (!challenge) {
      return '';
    }
    hydraSessionStore.setItem(AuthStorage.consent_challenge, challenge);
    return challenge;
  }

  private getLogoutChallenge(): string {
    // get challenge from query string param named 'logout_challenge'
    const urlParams = new URLSearchParams(window.location.search);
    const challenge = urlParams.get(AuthStorage.logout_challenge);
    if (!challenge) {
      // if no challenge, check local storage (we are likely returning from the workforce logout)
      const challengeFromLocalStorage = hydraSessionStore.getItem<string>(AuthStorage.logout_challenge);
      if (!!challengeFromLocalStorage) {
        return challengeFromLocalStorage;
      }
      return '';
    }
    hydraSessionStore.setItem(AuthStorage.logout_challenge, challenge);
    return challenge;
  }
}

const hydraShellService = new HydraShellService();
export { hydraShellService };

export type { HydraShellService };
